import { type ReactNode, type MouseEventHandler } from "react";
import { clsx } from "clsx";

interface ButtonProps {
  children: ReactNode;
  variant?: "gradient" | "outline" | "ghost" | "text";
  size?: "sm" | "md" | "lg";
  href?: string;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  type?: "button" | "submit";
  disabled?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: "px-4 py-2 text-sm",
  md: "px-6 py-2.5 text-sm",
  lg: "px-8 py-3.5 text-base",
};

const textSizeClasses = {
  sm: "text-sm",
  md: "text-sm",
  lg: "text-base",
};

export default function Button({
  children,
  variant = "gradient",
  size = "md",
  href,
  onClick,
  type = "button",
  disabled = false,
  className,
}: ButtonProps) {
  const isText = variant === "text";

  const baseClasses = isText
    ? "inline-flex items-center gap-1.5 font-semibold transition-colors duration-300 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
    : "inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-accent-purple/50 focus:ring-offset-2 focus:ring-offset-bg-primary disabled:opacity-50 disabled:cursor-not-allowed";

  const variantClasses = {
    gradient: clsx(
      "bg-gradient-cta text-white",
      "hover:shadow-lg hover:shadow-accent-purple/25 hover:scale-[1.02]",
      "active:scale-[0.98]"
    ),
    outline: clsx(
      "border border-surface-border text-gray-300 bg-transparent",
      "hover:border-accent-purple/50 hover:text-white hover:bg-surface/50",
      "active:scale-[0.98]"
    ),
    ghost: clsx(
      "text-gray-400 bg-transparent",
      "hover:text-white hover:bg-surface/50",
      "active:scale-[0.98]"
    ),
    text: clsx(
      "text-white bg-transparent",
      "hover:text-gray-300"
    ),
  };

  const classes = clsx(
    baseClasses,
    variantClasses[variant],
    isText ? textSizeClasses[size] : sizeClasses[size],
    className
  );

  if (href) {
    return (
      <a href={href} onClick={onClick} className={classes}>
        {children}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
    >
      {children}
    </button>
  );
}
